import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PageWithHeadline from '@/components/layout/PageWithHeadline';
import { PageContentCard } from '@/components/layout/PageContentCard';
import { TableToolbar, FloatingActionBar, FloatingDeleteButton } from '@/components';
import { useCompanies, useCompanyLevels, useDeleteCompanies } from './hooks';
import {
  CompaniesTable,
  CategoryFilter,
  CompanySidePanelHandler,
} from './components';
import { useSidePanelHandler } from '@/hooks/useSidePanelHandler';
import type { Company, CompanyQueryParams, CompanySidePanelContent } from './types';
import type { SortState } from '@/components/tables/SortDirection';
import useCachedValueIfNotNull from '@/utils/CachedValueIf';
import Pagination from '@/components/tables/Pagination';
import useDebounce from '@/utils/useDebounce';
import { useSearch } from '@tanstack/react-router';
import { useCurrentCompany } from '@/hooks/useCurrentCompany';
interface CompaniesSearchParams {
  page?: number;
  pageSize?: number;
  search?: string;
  categories?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}


function setSearchParams(params: CompaniesSearchParams) {
  const url = new URL(window.location.href);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    } else {
      url.searchParams.delete(key);
    }
  });
  window.history.replaceState({}, '', url.toString());
}

export function CompaniesPage() {
  const { t } = useTranslation();
  const searchParams = useSearch({ from: '/_app/companies' })

  const companyId = useCurrentCompany()


  //const [searchParams, setSearchParamsState] = useState<CompaniesSearchParams>(params);

  const [searchInput, setSearchInput] = useState(searchParams.search?.toString() || '');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const sidePanel = useSidePanelHandler<CompanySidePanelContent>();

  const debouncedSearch = useDebounce(searchInput);

  const page = searchParams.page || 1;
  const pageSize = searchParams.pageSize || 10;
  const selectedCategories = useMemo(
    () => (searchParams.categories !== undefined ? searchParams.categories.toString().split(',').map(Number) : []),
    [searchParams.categories]
  );
  const sortState: SortState = useMemo(
    () => ({
      column: searchParams.sortBy || null,
      direction: searchParams.sortOrder || null,
    }),
    [searchParams.sortBy, searchParams.sortOrder]
  );

  const queryParams: CompanyQueryParams = useMemo(
    () => {
      return {
        page,
        pageSize,
        search: debouncedSearch || undefined,
        categories:
          selectedCategories.length > 0 ? selectedCategories : undefined,
        sortBy: sortState.column || undefined,
        sortOrder: sortState.direction || undefined,
      }
    },
    [page, pageSize, debouncedSearch, selectedCategories, sortState]
  );

  const { data: companiesDataResult, isLoading: isLoadingCompanies, isFetching } = useCompanies(queryParams);
  const { data: categoriesData, isLoading: isLoadingCategories } = useCompanyLevels(companyId.companyId);
  const deleteCompanies = useDeleteCompanies();

  const serverCompanies = useCachedValueIfNotNull(companiesDataResult, isLoadingCompanies, { data: [], total: 0, page: 1, pageSize: 10 });

  const total = companiesDataResult?.total || 0;
  const categories: Record<number, string> = (categoriesData || []).reduce((acc, category) => {
    acc[category.hierarchy ?? 1000] = category.name;
    return acc;
  }, {} as Record<number, string>);


  const companies = useMemo(() => {
    const searchLower = searchInput.toLowerCase();
    return serverCompanies.data.filter(
      (company) =>
        company.name.toLowerCase().includes(searchLower)
    );
  }, [serverCompanies, searchInput, debouncedSearch, isLoadingCompanies]);

  const updateSearchParams = useCallback(
    (updates: Partial<CompaniesSearchParams>) => {
      const newParams: CompaniesSearchParams = { ...searchParams, ...updates };

      setSearchParams(newParams);
      //setSearchParamsState(newParams);
    },
    [searchParams]
  );

  useEffect(() => {
    if (debouncedSearch !== (searchParams.search || '')) {
      updateSearchParams({
        search: debouncedSearch || undefined,
        page: 1,
      });
    }
  }, [debouncedSearch, searchParams.search, updateSearchParams]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      updateSearchParams({ page: newPage });
      setSelectedIds(new Set());
    },
    [updateSearchParams]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      updateSearchParams({ pageSize: newPageSize, page: 1 });
      setSelectedIds(new Set());
    },
    [updateSearchParams]
  );

  const handleCategoryChange = useCallback(
    (newCategories: number[]) => {
      updateSearchParams({
        categories: newCategories.length > 0 ? newCategories.join(',') : undefined,
        page: 1,
      });
      setSelectedIds(new Set());
    },
    [updateSearchParams]
  );

  const handleSortChange = useCallback(
    (column: string) => {
      let newDirection: 'asc' | 'desc' | undefined;

      if (sortState.column === column) {
        if (sortState.direction === 'asc') {
          newDirection = 'desc';
        } else if (sortState.direction === 'desc') {
          newDirection = undefined;
        } else {
          newDirection = 'asc';
        }
      } else {
        newDirection = 'asc';
      }

      updateSearchParams({
        sortBy: newDirection ? column : undefined,
        sortOrder: newDirection,
        page: 1,
      });
    },
    [sortState, updateSearchParams]
  );

  const handleRowClick = useCallback((company: Company) => {
    sidePanel.open({ type: 'view', company: { name: company.name, uuid: company.id } });
  }, [sidePanel]);


  const handleAddCompany = useCallback(() => {
    sidePanel.open({ type: 'create' });
  }, [sidePanel]);

  const handleContentChange = useCallback((content: CompanySidePanelContent) => {
    sidePanel.open(content);
  }, [sidePanel]);


  return (
    <PageWithHeadline headlineKey="companies.title">
      <PageContentCard className="space-y-4 flex-1 min-h-0 flex flex-col w-full">
        <TableToolbar
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          searchBoxProps={{
            placeholder: t('companies.search.placeholder'),
            ariaLabel: t('companies.search.label'),
            clearAriaLabel: t('companies.search.clear'),
          }}
          onAdd={handleAddCompany}
          addLabel={t('companies.addButton')}
        >
          <CategoryFilter
            categories={categories}
            selectedCategories={selectedCategories}
            onCategoryChange={handleCategoryChange}
            isLoading={isLoadingCategories}
          />
        </TableToolbar>

        <div className="relative grow min-h-0">
          {isFetching && !isLoadingCompanies && (
            <div className="absolute top-2 right-2 z-10">
              <div className="animate-spin size-5 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          )}
          <CompaniesTable
            companies={companies}
            isLoading={isLoadingCompanies}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            sortState={sortState}
            onSortChange={handleSortChange}
            onRowClick={handleRowClick}
          />
        </div>

        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </PageContentCard>




      <CompanySidePanelHandler
        content={sidePanel.content}
        panelKey={sidePanel.panelKey}
        isOpen={sidePanel.isOpen}
        onClose={sidePanel.close}
        onContentChange={handleContentChange}
      />

      <FloatingActionBar selectedCount={selectedIds.size}>
        <FloatingDeleteButton
          onDelete={async () => {
            try {
              await deleteCompanies.mutateAsync(Array.from(selectedIds));
              setSelectedIds(new Set());
            } catch (error) {
              console.error('Failed to delete companies:', error);
            }
          }}
          isDeleting={deleteCompanies.isPending}
          modalTitle={t('companies.deleteModal.title')}
          modalMessage={t('companies.deleteModal.message')}
        />
      </FloatingActionBar>
    </PageWithHeadline>
  );
}
